import { Link } from 'react-router-dom';
import { Mic, Sparkles, Handshake, ArrowUpRight } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import PageHero from '@/components/sections/PageHero';
import { PHOTOS, STAN_STRATEGY_URL, SPEAKING_TOPICS } from '@/constants/content';
import { WORK } from '@/constants/testIds';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

export default function WorkWithMe() {
  usePageMeta({ ...SEO.workWithMe });
  return (
    <div className="bg-ivory">
      <section className="pt-40 lg:pt-56 pb-16">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow text-taupe mb-8">Work With Me · Three Ways</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
              Three ways
              <br />
              to <em className="italic text-taupe">engage.</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-matte/70 leading-relaxed text-[15px] max-w-md">
              Whether you&rsquo;re booking a stage, seeking strategic clarity, or exploring a
              partnership, here&rsquo;s how to work with Toia.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 lg:py-12">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <PageHero
            src={PHOTOS.work}
            alt="Toia Wallace"
            mobileAnchor="top"
            desktopAnchor="center"
            desktopHeight="md:h-[60vh] lg:h-[70vh]"
            testId="workwithme-hero"
          />
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Speaking */}
            <article
              data-testid={WORK.speakingCard}
              className="group bg-white rounded-2xl p-10 luxury-shadow border border-transparent hover:border-gold/40 transition-[border-color,transform] duration-500 hover:-translate-y-1 flex flex-col"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="w-11 h-11 rounded-full bg-ivory border border-taupe/40 grid place-items-center">
                  <Mic size={18} strokeWidth={1.4} className="text-matte" />
                </span>
                <span className="eyebrow text-taupe">01</span>
              </div>
              <h3 className="font-serif-display text-4xl tracking-tighter text-matte leading-tight mb-4">Speaking Engagements</h3>
              <p className="text-matte/70 leading-relaxed mb-8 flex-1">
                Invite Toia Wallace to speak at conferences, corporate events, universities,
                workshops, leadership events, podcasts, and private organizations.
              </p>
              <div className="mb-8">
                <div className="eyebrow text-taupe mb-3">Topics</div>
                <ul className="space-y-1 text-sm text-matte/70">
                  {SPEAKING_TOPICS.slice(0, 5).map((t) => <li key={t}>· {t}</li>)}
                </ul>
              </div>
              <Link
                to="/speaking#inquiry"
                data-testid={WORK.ctaSpeaking}
                className="btn-primary self-start mt-auto"
              >
                Book Toia Wallace
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
            </article>

            {/* Strategy Intensive, featured */}
            <article
              data-testid={WORK.strategyCard}
              className="group bg-matte text-ivory rounded-2xl p-10 luxury-shadow border border-gold/40 relative overflow-hidden flex flex-col"
            >
              <div className="absolute top-0 right-0 px-4 py-2 bg-gold text-matte text-[10px] font-label tracking-widest uppercase rounded-bl-2xl">Most Requested</div>
              <div className="flex items-center justify-between mb-8">
                <span className="w-11 h-11 rounded-full bg-gold/15 border border-gold/40 grid place-items-center">
                  <Sparkles size={18} strokeWidth={1.4} className="text-gold" />
                </span>
                <span className="eyebrow text-gold">02</span>
              </div>
              <h3 className="font-serif-display text-4xl tracking-tighter leading-tight mb-4">
                Strategy Intensive
              </h3>
              <p className="text-ivory/75 leading-relaxed mb-6 flex-1">
                A 90-minute private, one-on-one strategy session for entrepreneurs, founders,
                executives, and business owners seeking clarity, expert guidance, and actionable next steps.
              </p>
              <div className="mb-8">
                <div className="eyebrow text-gold mb-2">Investment</div>
                <div className="font-serif-display text-5xl tracking-tighter">$297</div>
                <div className="text-xs text-ivory/50 font-label tracking-widest uppercase mt-2">90-Minute Session</div>
              </div>
              <a
                href={STAN_STRATEGY_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={WORK.ctaStrategy}
                className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte self-start mt-auto"
              >
                Book Your Strategy Intensive
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </article>

            {/* Partnerships */}
            <article
              data-testid={WORK.partnershipsCard}
              className="group bg-white rounded-2xl p-10 luxury-shadow border border-transparent hover:border-gold/40 transition-[border-color,transform] duration-500 hover:-translate-y-1 flex flex-col"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="w-11 h-11 rounded-full bg-ivory border border-taupe/40 grid place-items-center">
                  <Handshake size={18} strokeWidth={1.4} className="text-matte" />
                </span>
                <span className="eyebrow text-taupe">03</span>
              </div>
              <h3 className="font-serif-display text-4xl tracking-tighter text-matte leading-tight mb-4">Strategic Partnerships</h3>
              <p className="text-matte/70 leading-relaxed mb-8 flex-1">
                For organizations, brands, investors, media opportunities, and collaboration
                inquiries. This is where meaningful conversations begin.
              </p>
              <div className="mb-8">
                <div className="eyebrow text-taupe mb-3">Best For</div>
                <ul className="space-y-1 text-sm text-matte/70">
                  <li>· Brand Partnerships</li>
                  <li>· Corporate Collaborations</li>
                  <li>· Media Opportunities</li>
                  <li>· Investment Conversations</li>
                  <li>· Strategic Alliances</li>
                </ul>
              </div>
              <Link
                to="/contact?kind=partnership"
                data-testid={WORK.ctaPartnerships}
                className="btn-primary self-start mt-auto"
              >
                Start a Conversation
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
            </article>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <SectionMarker number="04" label="A note" className="justify-center" />
          <p className="mt-10 font-serif-display italic text-3xl md:text-4xl leading-snug tracking-tight text-matte">
            &ldquo;I am not offering services. I am offering a room. If you sit in it well, you leave with something
            you cannot buy anywhere else, clarity.&rdquo;
          </p>
        </div>
      </section>
    </div>
  );
}
